import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import {
  ManagerProgressionCategorySlug,
  ManagerProgressionTaskState,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { MUIBadge } from '@sorare/core/src/atoms/ui/MUIBadge';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useOneTimeDialogContext } from '@sorare/core/src/contexts/oneTimeDialog';
import { OneTimeDialog } from '@sorare/core/src/contexts/oneTimeDialog/Provider';
import { isType } from '@sorare/core/src/gql';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { LIFECYCLE } from '@sorare/core/src/hooks/useLifecycle';
import useEvents from '@sorare/core/src/lib/events/useEvents';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CardManagerId } from 'components/managerId/CardManagerId';

import { Category } from '../Category';
import { GenericSkinItem } from '../Category/Task';
import { ChallengesIntroduceDialog } from '../ChallengesIntroduceDialog';
import { ClaimBadgeAnimation } from '../ClaimBadgeAnimation';
import gridPattern from '../assets/grid_pattern.svg';
import {
  ChallengesQuery,
  ChallengesQueryVariables,
} from './__generated__/index.graphql';

const CHALLENGES_QUERY = gql`
  query ChallengesQuery($sport: Sport!, $slug: String!) {
    user(slug: $slug) {
      slug
      managerProgression(sport: $sport) {
        id
        categories(type: MANAGER_PROGRESSION_CHALLENGES) {
          id
          name
          ...Category_ManagerProgressionCategory
          ...ClaimBadgeAnimation_ManagerProgressionCategory
        }
        ...CardManagerId_managerProgression
      }
      userSportProfile(sport: $sport) {
        ... on UserSportProfileInterface {
          id
          ...CardManagerId_userSportProfile
        }
      }
      ...CardManagerId_user
    }
  }
  ${Category.fragments.managerProgressionCategory}
  ${ClaimBadgeAnimation.fragments.managerProgressionCategory}
  ${CardManagerId.fragments.user}
  ${CardManagerId.fragments.userSportProfile}
  ${CardManagerId.fragments.managerProgression}
` as TypedDocumentNode<ChallengesQuery, ChallengesQueryVariables>;

const Root = styled(FullWidthContainer)`
  min-height: inherit;
  padding: var(--intermediate-unit) var(--container-padding);
  background:
    radial-gradient(
      80% 80% at top right,
      #1f403a 20%,
      rgba(88, 255, 224, 0) 100%
    ),
    repeat-x url(${gridPattern});
`;
const Content = styled.div`
  padding-top: calc(5 * var(--unit));
`;
const ManagerIdContainer = styled.div`
  width: 100%;
  margin-left: auto;

  @media ${laptopAndAbove} {
    min-width: 400px;
    width: auto;
  }
`;

type Props = {
  slug: string;
  sport: Sport;
};

export const Challenges = ({ slug, sport }: Props) => {
  const { sawDialog } = useOneTimeDialogContext();
  const track = useEvents();
  const { data, loading } = useQuery(CHALLENGES_QUERY, {
    variables: { slug, sport },
  });
  const { currentUser } = useCurrentUserContext();
  const { isIosApp } = useIsMobileApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showClaimAnimation, setShowClaimAnimation] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [rewards, setRewards] = useState<GenericSkinItem[]>([]);
  const [previousProgression, setPreviousProgression] = useState(
    data?.user?.managerProgression
  );

  const isMe = slug === currentUser?.slug;

  const activeCategory = useMemo(
    () =>
      data?.user?.managerProgression?.categories?.find(
        category => category.name === searchParams.get('category')
      ) || data?.user?.managerProgression?.categories?.[0],
    [data?.user?.managerProgression?.categories, searchParams]
  );

  if (!previousProgression && data?.user?.managerProgression) {
    setPreviousProgression(data?.user?.managerProgression);
  }

  if (loading) return <LoadingIndicator fullHeight />;
  if (!data?.user?.managerProgression) return null;

  const { managerProgression } = data.user;

  const sortedCategoriesSlugs = [
    ManagerProgressionCategorySlug.ONBOARDING,
    ManagerProgressionCategorySlug.UNRIVALLED,
    ManagerProgressionCategorySlug.TACTICIAN,
    ManagerProgressionCategorySlug.COMMON_PRO,
    ManagerProgressionCategorySlug.LIMITED_PRO,
    ManagerProgressionCategorySlug.RARE_PRO,
    ManagerProgressionCategorySlug.SUPER_RARE_PRO,
    ManagerProgressionCategorySlug.UNIQUE_PRO,
    ManagerProgressionCategorySlug.COLLECTOR,
    ManagerProgressionCategorySlug.INFLUENCER,
  ];
  const sortedCategories = [...(managerProgression.categories || [])].sort(
    (a, b) => {
      return (
        sortedCategoriesSlugs.indexOf(a.name) -
        sortedCategoriesSlugs.indexOf(b.name)
      );
    }
  );

  return (
    <>
      {!isIosApp && <PageHeader text={glossary.challenges} />}
      <Root>
        <Vertical>
          <ManagerIdContainer>
            <CardManagerId
              user={data.user}
              managerProgression={data.user.managerProgression}
              userSportProfile={data.user.userSportProfile}
              disableLink={isIosApp}
            />
          </ManagerIdContainer>
          <TabBar value={activeCategory?.name} transparent variant="flat">
            {sortedCategories.map(category => {
              const nbTasksToClaim = category.categoryTasks.filter(
                task =>
                  isType(task, 'ManagerProgressionTask') &&
                  task.aasmState === ManagerProgressionTaskState.COMPLETED
              ).length;

              return (
                <Tab
                  key={category.name}
                  value={category.name}
                  onClick={() => {
                    setSearchParams(
                      { category: category.name },
                      { replace: true }
                    );
                    track('Click Manager Progression Category', {
                      categoryName: category.name,
                      claimableTasks: nbTasksToClaim,
                    });
                  }}
                >
                  <Horizontal gap={2}>
                    <span>{category.title}</span>
                    {!!nbTasksToClaim && isMe && (
                      <MUIBadge badgeContent={nbTasksToClaim} />
                    )}
                  </Horizontal>
                </Tab>
              );
            })}
          </TabBar>
          {activeCategory && (
            <>
              <Content>
                <Category
                  category={activeCategory}
                  onClaim={({ rewards: claimedRewards, pointsEarned: pts }) => {
                    setPointsEarned(pts);
                    setRewards(claimedRewards);
                    setShowClaimAnimation(true);
                  }}
                  isMe={isMe}
                />
              </Content>
              <Portal id="full-screen-animation">
                {previousProgression &&
                  !!pointsEarned &&
                  showClaimAnimation && (
                    <ClaimBadgeAnimation
                      open
                      onClose={() => {
                        setShowClaimAnimation(false);
                        setPreviousProgression(managerProgression);
                        setRewards([]);
                        setPointsEarned(0);
                      }}
                      category={activeCategory}
                      from={previousProgression}
                      to={managerProgression}
                      pointsEarned={pointsEarned}
                      rewards={rewards}
                    />
                  )}
              </Portal>
            </>
          )}
        </Vertical>
      </Root>
      <OneTimeDialog
        dialogId={LIFECYCLE.sawChallengesOnboarding}
        show={!sawDialog(LIFECYCLE.sawChallengesOnboarding)}
      >
        {({ onClose, open }) => (
          <ChallengesIntroduceDialog open={open} onClose={onClose} />
        )}
      </OneTimeDialog>
    </>
  );
};

const ChallengesPage = ({ sport }: { sport: Sport }) => {
  const { slug } = useParams();

  if (!slug) {
    return null;
  }
  return <Challenges slug={slug} sport={sport} />;
};

export default ChallengesPage;
